/* eslint-disable no-nested-ternary */
import { message } from 'antd';
import React, { useEffect, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Typography } from 'tfc-components';

import Icon from 'components/atoms/Icon';
import Loading from 'components/atoms/Loading';
import Section from 'components/organisms/Section';
import { checkPaymentStatus, checkPaymentStatusCancel } from 'services/accounts';
import { IAlePaySuccesResponnse } from 'services/accounts/types';
import { ROUTE_PATHS } from 'utils/constants';
import mapModifiers from 'utils/functions';

const PaymentStatus: React.FC = () => {
  const { status } = useParams<{ status: string }>();
  const [searchParams] = useSearchParams();
  const [dataResponse, setDataResponse] = useState<IAlePaySuccesResponnse>();
  const {
    transactionCode, errorCode, cancel, merchantSideUserId
  } = Object.fromEntries(searchParams);
  const navigate = useNavigate();

  const { mutate: handleCheckingPayment, isLoading: isLoadingChecking } = useMutation(
    ['checkingPayment'],
    checkPaymentStatus,
    {
      onSuccess(data) {
        if (data) {
          setDataResponse(data);
        }
        localStorage.removeItem('paymentChecked');
      },
      onError(error: any) {
        if (Array.isArray(error)) {
          error.forEach((err) => {
            message.error(err.message);
          });
        }
        const paymentChecked = localStorage.getItem('paymentChecked');
        if (paymentChecked) {
          navigate(`${ROUTE_PATHS.CHECKOUT}/payment-status/cancel`);
        }
      }
    }
  );

  const { mutate: handleCheckingPaymentCancel, isLoading: isLoadingCheckingCancel } = useMutation(
    ['checkingPaymentCancel'],
    checkPaymentStatusCancel,
    {
      onSuccess() {
      },
      onError(error: any) {
        message.error(error.response.message);
      },
    }
  );

  useEffect(() => {
    if (
      transactionCode
      || merchantSideUserId
    ) {
      if (status === 'success' && cancel === 'false') {
        if (merchantSideUserId) {
          handleCheckingPayment({
            errorCode,
            merchantSideUserId,
            cancel,
          });
        } else {
          handleCheckingPayment({
            transactionCode,
            errorCode,
            cancel,
          });
        }
      } else {
        handleCheckingPaymentCancel({
          transactionCode,
          errorCode,
          cancel,
        });
      }
    }
  }, [transactionCode,
    merchantSideUserId,
    errorCode, cancel,
    handleCheckingPayment,
    handleCheckingPaymentCancel]);

  const handleNavigate = () => {
    const paymentChecked = localStorage.getItem('paymentChecked');
    if (status === 'success') {
      localStorage.removeItem('paymentChecked');
      return navigate('/');
    }
    if (paymentChecked) {
      return navigate(`${ROUTE_PATHS.CHECKOUT}?token=${paymentChecked}`);
    }

    return navigate(ROUTE_PATHS.CHECKOUT);
  };

  return isLoadingChecking || isLoadingCheckingCancel ? <div className="p-checkout_paymentStatus_loading"><Loading /></div> : (
    <Section className="p-checkout_paymentStatus">
      <div className="p-checkout_paymentStatus_container">
        <div className={mapModifiers('p-checkout_paymentStatus_icon', status === 'success' && 'success')}>
          <Icon iconName={status === 'success' ? 'checkWhite' : 'warningWhite'} size="30" />
        </div>
        <div className="p-checkout_paymentStatus_wrap">
          <Typography.Text fontweight="600" extendClasses="p-checkout_paymentStatus_text fs-24x30">
            {status === 'success' ? 'Payment successful' : 'Payment failed'}
          </Typography.Text>
          <Typography.Text extendClasses="p-checkout_paymentStatus_description fs-18x21">
            {status === 'success' ? (
              dataResponse?.billingType === 1 || dataResponse?.isSaveCardInfo ? (
                <>
                  Your payment has been processed successfully. A confirmation email has
                  <br />
                  been sent to your email
                  {' '}
                  {dataResponse?.email}
                </>
              )
                : (
                  <>
                    Heads up! Your payment was successful, but your card was not saved.
                    <br />
                    Please add a card in the Billing module to ensure uninterrupted service
                  </>
                )
            ) : (
              <>
                Oops! Something went wrong with your payment.
                <br />
                Try again or contact support.
              </>
            )}
          </Typography.Text>
        </div>
        <div className={mapModifiers('p-checkout_paymentStatus_buttonWrap', status === 'cancel' && 'failed')}>
          {status !== 'success' && <Button disabled={isLoadingChecking || isLoadingCheckingCancel} extendClasses="p-checkout_paymentStatus_button p-checkout_paymentStatus_buttonContact">Contact support</Button>}
          <Button disabled={isLoadingChecking || isLoadingCheckingCancel} onClick={() => handleNavigate()} variant="primary" extendClasses="p-checkout_paymentStatus_button">{status === 'success' ? 'Back to Home Page' : 'Try again'}</Button>
        </div>
      </div>
    </Section>
  );
};

export default PaymentStatus;
